import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Camera, File, Loader2 } from "lucide-react";
import { clean, format, validate } from "rut.js";
import { z } from "zod";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { SalesRequestSelect } from "~/pages/clients/components/client-detail/payment-proof/SalesRequestSelect";
import { PaymentProof } from "~/types/payment";

// Validation schema
const paymentProofSchema = z.object({
  rut: z
    .string()
    .min(1, "El RUT es requerido")
    .max(12, "El RUT no puede tener más de 12 caracteres")
    .refine((value) => validate(value), {
      message: "El RUT ingresado no es válido",
    }),
  amount: z
    .number({ required_error: "El monto es requerido" })
    .min(1, "El monto debe ser mayor a 0"),
  salesRequestId: z.string().optional(),
  comments: z.string().optional(),
});

type PaymentProofFormValues = z.infer<typeof paymentProofSchema>;

interface PaymentProofFormProps {
  clientId: string;
  initialData?: PaymentProof;
  onSubmit: (data: PaymentProofFormValues, file?: File) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  isUploading?: boolean;
  disabled?: boolean;
}

export const PaymentProofForm = ({
  clientId,
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  isUploading = false,
  disabled = false,
}: PaymentProofFormProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const isEditing = !!initialData;

  // Initialize form
  const form = useForm<PaymentProofFormValues>({
    resolver: zodResolver(paymentProofSchema),
    defaultValues: {
      rut: initialData?.rut || "",
      amount: initialData?.amount || 0,
      salesRequestId: initialData?.salesRequestId || "",
      comments: initialData?.comments || "",
    },
  });

  // Set preview image when editing
  useEffect(() => {
    if (initialData?.screenshotUrl) {
      setPreviewUrl(initialData.screenshotUrl);
    }
  }, [initialData]);

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Format RUT as user types
  const handleRutChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const value = e.target.value;
    // Only proceed if the input is within the allowed length
    if (clean(value).length <= 9) {
      // 9 digits without formatting, which becomes max 12 chars with formatting
      const formattedRut = format(value);
      onChange(formattedRut);
    }
  };

  // Handle form submission
  const handleSubmit = (values: PaymentProofFormValues) => {
    // Clean RUT before submitting
    const cleanedValues = {
      ...values,
      rut: clean(values.rut),
      salesRequestId: values.salesRequestId ? values.salesRequestId : undefined,
      comments: values.comments ? values.comments : undefined,
    };
    onSubmit(cleanedValues, selectedFile || undefined);
  };

  const isProcessing = isSubmitting || isUploading || disabled;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <Alert className="mb-4 border-blue-200 bg-blue-50">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-700">
            Al registrar un comprobante de pago, el sistema intentará vincularlo
            con transacciones bancarias existentes.
          </AlertDescription>
        </Alert>

        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="rut"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>RUT</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ej: 12345678-9"
                      autoComplete="off"
                      {...field}
                      onChange={(e) => handleRutChange(e, field.onChange)}
                      disabled={isProcessing}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Monto</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Ej: 50000"
                      autoComplete="off"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      disabled={isProcessing}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="salesRequestId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium">
                    Nota de Venta (Recomendado)
                  </FormLabel>
                  <FormControl>
                    <SalesRequestSelect
                      clientId={clientId}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isProcessing}
                    />
                  </FormControl>
                  <div className="text-xs text-muted-foreground">
                    Seleccione la nota de venta asociada a este pago para
                    vincular automáticamente las transacciones.
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <FormField
          control={form.control}
          name="comments"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Comentarios (Opcional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Agregar comentarios o notas sobre este pago..."
                  {...field}
                  disabled={isProcessing}
                  className="resize-none"
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <FormLabel>Comprobante (Opcional)</FormLabel>
          <div className="flex flex-col gap-4">
            {previewUrl && (
              <div className="relative h-40 w-full overflow-hidden rounded-md border">
                <img
                  src={previewUrl}
                  alt="Comprobante"
                  className="h-full w-full object-contain"
                />
              </div>
            )}

            <div className="flex gap-2">
              <label
                htmlFor="file-upload"
                className="flex cursor-pointer items-center rounded-md bg-gray-100 px-4 py-2 hover:bg-gray-200"
              >
                <input
                  id="file-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isProcessing}
                />
                <Camera className="mr-2 h-4 w-4" />
                <span>
                  {selectedFile ? "Cambiar imagen" : "Seleccionar imagen"}
                </span>
              </label>

              {selectedFile && (
                <div className="flex items-center text-sm text-gray-500">
                  <File className="mr-1 h-4 w-4" />
                  <span className="max-w-[200px] truncate">
                    {selectedFile.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={isProcessing}>
            {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Actualizar" : "Guardar"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
